import React from 'react';

import type * as api from '@gitbook/api-client';
import { Description, Label, Radio, RadioCheck, RadioGroup } from '@gitbook/spine-react';
import { deepEquals } from '@gitbook/util-immutable';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { MergeRulesEditor } from './MergeRulesEditor';
import { MergeRulesInheritEditor } from './MergeRulesInheritEditor';

export type MergeRulesEditorProps = {
    configuration: api.MergeRulesSpaceConfiguration;
    onChange: (configuration: api.MergeRulesSpaceConfiguration) => void;
    /**
     * Whether to allow the user to inherit the merge rule from the organization.
     */
    allowInherit?: boolean;
};

/**
 * Easy hook to manage the state of editing merge rules configuration.
 */
export function useMergeRulesConfigurationEditor(
    initialConfiguration: api.MergeRulesSpaceConfiguration
) {
    const [configuration, setConfiguration] = useStateWithDeps(initialConfiguration, [
        initialConfiguration,
    ]);

    const hasChanges = React.useMemo(() => {
        return !deepEquals(configuration, initialConfiguration);
    }, [configuration, initialConfiguration]);

    return {
        configuration,
        onChange: setConfiguration,
        hasChanges,
    };
}

/**
 * Editor for a merge rule configuration.
 */
export function MergeRulesConfigurationEditor(props: MergeRulesEditorProps) {
    const { configuration, onChange, allowInherit } = props;

    return (
        <div>
            <RadioGroup
                aria-label="Merge rules configuration"
                value={configuration.type}
                onChange={(newValue: api.MergeRulesSpaceConfiguration['type']) => {
                    switch (newValue) {
                        case 'inherit':
                            onChange({ type: 'inherit' });
                            break;
                        case 'rules':
                            onChange({ type: 'rules', rules: [] });
                            break;
                        case 'none':
                            onChange({ type: 'none' });
                            break;
                        default:
                            assertNever(newValue);
                    }
                }}
                className="flex flex-col gap-6"
            >
                {allowInherit ? (
                    <MergeRulesConfigurationTypeRadio
                        type="inherit"
                        activeConfiguration={
                            configuration.type === 'inherit' ? configuration : null
                        }
                        onChange={onChange}
                        label="Use organization configuration"
                        description="Inherit the merge rule configuration from the organization."
                        configurationComponent={MergeRulesInheritEditor}
                    />
                ) : null}
                <MergeRulesConfigurationTypeRadio
                    type="none"
                    activeConfiguration={configuration.type === 'none' ? configuration : null}
                    onChange={onChange}
                    label="Allow unrestricted merging"
                    description="Change requests can be merged without any rules by any member with sufficient permissions."
                />
                <MergeRulesConfigurationTypeRadio
                    type="rules"
                    activeConfiguration={configuration.type === 'rules' ? configuration : null}
                    onChange={onChange}
                    configurationComponent={MergeRulesEditor}
                    label="Restrict merging with rules"
                    description="Use and combine rules to determine if change requests can be merged."
                />
            </RadioGroup>
        </div>
    );
}

function MergeRulesConfigurationTypeRadio<
    ConfigType extends api.MergeRulesSpaceConfiguration['type'],
>(props: {
    label: string;
    description: string;
    type: ConfigType;
    activeConfiguration: null | Extract<api.MergeRulesSpaceConfiguration, { type: ConfigType }>;
    onChange: (mergeRules: Extract<api.MergeRulesSpaceConfiguration, { type: ConfigType }>) => void;
    configurationComponent?: React.ComponentType<{
        configuration: Extract<api.MergeRulesSpaceConfiguration, { type: ConfigType }>;
        onChange: (
            mergeRules: Extract<api.MergeRulesSpaceConfiguration, { type: ConfigType }>
        ) => void;
    }>;
}) {
    const {
        label,
        description,
        type,
        activeConfiguration,
        onChange,
        configurationComponent: ConfigurationComponent,
    } = props;

    return (
        <div className="flex flex-col gap-3">
            <Radio value={type} className="flex flex-row items-center gap-4" aria-label={label}>
                <RadioCheck />
                <div>
                    <Label>{label}</Label>
                    <Description>{description}</Description>
                </div>
            </Radio>
            {activeConfiguration && ConfigurationComponent ? (
                <div className="ml-9">
                    <ConfigurationComponent
                        configuration={activeConfiguration}
                        onChange={onChange}
                    />
                </div>
            ) : null}
        </div>
    );
}
